import { LicencedIntervenor } from "@/Common/Api/MedfarDictionaries/Dto/Responses/LicencedIntervenor";
import { PatientRefIntervenorWithDetails } from "@/Common/Api/Patient/Dto/PatientRefIntervenorWithDetails";
import { Guid } from "@/Common/Models/Guid";
import { TitleAndSpecialtyModel } from "@/Common/Models/MedfarDictionaries/TitleAndSpecialtyModel";
import { EditMode } from "@/Common/Models/Patient/PatientEnums";
import { PatientRefIntervenorWithDetailsModel } from "@/Common/Models/Patient/PatientRefIntervenorWithDetailsModel";
import { IPatientCliniciansModel } from "@/Common/Models/Patient/UI/Profile/IPatientCliniciansModel";
import { Person } from "@/Common/Models/Person/Person";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { ContactDirectoryClient } from "@/ContactDirectory/Api/ContactDirectoryClient";
import { DirectoryEntityType, DirectoryHealthCareProviderSearchDto } from "@/ContactDirectory/Api/medfar-contactdirectory/dist";
import { DirectoryHealthCareProvider } from "@/ContactDirectory/Models/DirectoryHealthCareProvider";
import { PatientStore } from "@/Patient/Stores/PatientStore";

class PatientProfileCliniciansHandler extends BaseOperationHandler {
    private contactDirectoryClient: ContactDirectoryClient;

    constructor() {
        super();
        this.contactDirectoryClient = ContactDirectoryClient.getInstance();

        this.RegisterOperation(LocalPatientOperations.Profile.Clinicians_EditClick, this.CliniciansEditClick);
        this.RegisterOperation(LocalPatientOperations.Profile.Clinicians_CancelClick, this.CliniciansCancelClick);
        this.RegisterOperation(LocalPatientOperations.Profile.Clinicians_SaveClick, this.CliniciansSaveClick);

        this.RegisterOperation(LocalPatientOperations.Profile.Clinicians_SetIntervenorEditMode, this.Clinicians_SetIntervenorEditMode);
        this.RegisterOperation(LocalPatientOperations.Profile.Clinicians_SelectDoctor, this.Clinicians_SelectDoctor);
        this.RegisterOperation(LocalPatientOperations.Profile.Clinicians_SelectDoctorEmployee, this.Clinicians_SelectDoctorEmployee);
        this.RegisterOperation(LocalPatientOperations.Profile.Clinicians_EditConsultant, this.editConsultant);
        this.RegisterOperation(LocalPatientOperations.Profile.Clinicians_SelectSpeciality, this.Clinicians_SelectSpeciality);
        this.RegisterOperation(LocalPatientOperations.Profile.Clinicians_ContactDirectory_ExternalProvider_Edit, this.cliniciansExternalProviderEdit);

        this.RegisterOperation(LocalPatientOperations.Profile.Clinicians_AddIntervenorOpen, this.Clinicians_AddIntervenorOpen);
        this.RegisterOperation(LocalPatientOperations.Profile.Clinicians_AddIntervenorClose, this.Clinicians_AddIntervenorClose);
        this.RegisterOperation(LocalPatientOperations.Profile.Clinicians_AddIntervenorSave, this.Clinicians_AddIntervenorSave);

        this.RegisterOperation(LocalPatientOperations.Profile.Clinicians_DeleteIntervenor, this.Clinicians_DeleteIntervenor);
        this.RegisterOperation(LocalPatientOperations.Profile.Clinicians_ClearIntervenorSpeciality, this.Clinicians_ClearIntervenorSpeciality);
        this.RegisterOperation(LocalPatientOperations.Profile.Clinicians_IsPrimaryChange, this.Clinicians_IsPrimaryChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Clinicians_OnNoteChange, this.Clinicians_OnNoteChange);

        this.RegisterOperation(LocalPatientOperations.Profile.Clinicians_LoadHealthCareProviders, this.loadHealthCareProviders);
        this.RegisterOperation(LocalPatientOperations.Profile.Clinicians_UpdateHealthCareProvider, this.updateCliniciansFromFaxDirectory);

        this.RegisterOperation(LocalPatientOperations.Profile.Clinicians_EditPatientFileAllowlist, this.ProtectPatientChartEdit);
    }

    private Clinicians_OnNoteChange(request: LocalEvent, store: PatientStore): void {
        var Id = request.Get<string>("Id");
        var Note = request.Get<string>("Note");
        var ClinicianUIModel = store.PatientProfileCliniciansModels[request.PatientId];

        store.PatientProfileCliniciansModels[request.PatientId] = this.MutateIntervenors(
            ClinicianUIModel,
            Id,
            item => item.Model.Intervenor.Note = Note
        );
    }

    private Clinicians_IsPrimaryChange(request: LocalEvent, store: PatientStore): void {
        var Id = request.Get<string>("Id");
        var ClinicianUIModel = store.PatientProfileCliniciansModels[request.PatientId];

        store.PatientProfileCliniciansModels[request.PatientId] = this.MutateIntervenors(
            ClinicianUIModel,
            Id,
            item => item.Model.Intervenor.IsPrimary = !(item.Model.Intervenor.IsPrimary == true) // Toggle but consider Null as False
        );

        let intervenors = ClinicianUIModel.get("IntervenorsOnFile").toArray();
        let specialityToLook = intervenors.filter(c => c.Model.Intervenor.Id === Id)[0].GetSpecialisationCategory();

        intervenors.filter(v => v.GetSpecialisationCategory().Name === specialityToLook.Name).forEach(b => {
            if (b.Model.Intervenor.Id != Id) {
                b.Model.Intervenor.IsPrimary = false;
            }

            return b;
        });

        ClinicianUIModel = ClinicianUIModel.set("IntervenorsOnFile", intervenors.toImmutable());
        store.PatientProfileCliniciansModels[request.PatientId] = ClinicianUIModel;
    }

    private Clinicians_DeleteIntervenor(request: LocalEvent, store: PatientStore): void {
        var Id = request.Get<string>("Id");
        var ClinicianUIModel = store.PatientProfileCliniciansModels[request.PatientId];

        var list = ClinicianUIModel.get("IntervenorsOnFile").toArray().filter(x => x.Model.Intervenor.Id != Id);
        ClinicianUIModel = ClinicianUIModel.set("IntervenorsOnFile", list.toImmutable());

        store.PatientProfileCliniciansModels[request.PatientId] = ClinicianUIModel;
    }

    private Clinicians_ClearIntervenorSpeciality(request: LocalEvent, store: PatientStore): void {
        var Id = request.Get<string>("Id");
        var ClinicianUIModel = store.PatientProfileCliniciansModels[request.PatientId];

        store.PatientProfileCliniciansModels[request.PatientId] = this.MutateIntervenors(
            ClinicianUIModel,
            Id,
            item => item.DeleteSpeciality()
        );
    }

    private Clinicians_AddIntervenorSave(request: LocalEvent, store: PatientStore): void {
        let pendingIntervenor = request.Get<PatientRefIntervenorWithDetailsModel>("intervenor");
        const IsAddPlus = request.Get<boolean>("IsAddPlus");

        let ClinicianUIModel = store.PatientProfileCliniciansModels[request.PatientId];

        const IntervenorsOnFile = ClinicianUIModel.get("IntervenorsOnFile").toArray();
        pendingIntervenor.IsEditMode = false;

        const isHealthCareProviderDuplicate = pendingIntervenor.Model.HealthCareProvider
        && IntervenorsOnFile.some(p =>
            p.Model.Intervenor?.HealthCareProviderId === pendingIntervenor.Model.HealthCareProvider?.id
        );

        const isLicencedIntervenorDuplicate = pendingIntervenor.Model.LicencedIntervenor
        && IntervenorsOnFile.some(p =>
            p.Model.LicencedIntervenor?.Id === pendingIntervenor.Model.LicencedIntervenor?.Id
        );

        if (!isHealthCareProviderDuplicate && !isLicencedIntervenorDuplicate)
            IntervenorsOnFile.push(pendingIntervenor);

        pendingIntervenor = PatientRefIntervenorWithDetailsModel.New(pendingIntervenor.SearchByEmployee, false);

        if (!IsAddPlus) {
            ClinicianUIModel = ClinicianUIModel.set("IsAddPopupOpen", false);
            sessionStore.hideGlobalOverlay();
        }

        ClinicianUIModel = ClinicianUIModel.set("PendingAddIntervenor", pendingIntervenor);

        ClinicianUIModel = ClinicianUIModel.set("IntervenorsOnFile", IntervenorsOnFile.toImmutable());
        store.PatientProfileCliniciansModels[request.PatientId] = ClinicianUIModel;
    }

    private Clinicians_AddIntervenorClose(request: LocalEvent, store: PatientStore): void {
        var ClinicianUIModel = store.PatientProfileCliniciansModels[request.PatientId];
        ClinicianUIModel = ClinicianUIModel.set("IsAddPopupOpen", false);
        store.PatientProfileCliniciansModels[request.PatientId] = ClinicianUIModel;

        sessionStore.hideGlobalOverlay();
    }

    private Clinicians_AddIntervenorOpen(request: LocalEvent, store: PatientStore): void {
        var IsInternal = request.Get<boolean>("IsInternal");

        var ClinicianUIModel = store.PatientProfileCliniciansModels[request.PatientId];
        ClinicianUIModel = ClinicianUIModel.set("PendingAddIntervenor", PatientRefIntervenorWithDetailsModel.New(IsInternal));
        ClinicianUIModel = ClinicianUIModel.set("IsAddPopupOpen", true);

        sessionStore.showGlobalOverlay(true);

        store.PatientProfileCliniciansModels[request.PatientId] = ClinicianUIModel;
    }

    private Clinicians_SetIntervenorEditMode(request: LocalEvent, store: PatientStore): void {
        const id = request.Get<string>("intervenorId");
        const setEditMode = request.Get<boolean>("setEditModel");
        const clinicianUIModel = store.PatientProfileCliniciansModels[request.PatientId];

        store.PatientProfileCliniciansModels[request.PatientId] = this.MutateIntervenors(
            clinicianUIModel,
            id,
            item => item.IsEditMode = setEditMode
        );
    }

    private Clinicians_SelectDoctorEmployee(request: LocalEvent, store: PatientStore): void {
        var Id = request.Get<string>("Id");
        var employeePerson = request.Get<Person>("value");
        var ClinicianUIModel = store.PatientProfileCliniciansModels[request.PatientId];

        if (employeePerson != null) {
            store.PatientProfileCliniciansModels[request.PatientId] = this.MutateIntervenors(
                ClinicianUIModel,
                Id,
                item => {
                    item.Model.Intervenor.EmployeeId = employeePerson.ContactFacetId;
                    item.InitEmployee();

                    if (item.Model.LicenceOrganisationRule != null)
                        item.IsEditMode = false;
                }
            );
        }
    }

    private Clinicians_SelectDoctor(request: LocalEvent, store: PatientStore): void {
        var Id = request.Get<string>("Id");
        var LicencedIntervenor = request.Get<LicencedIntervenor>("value");
        var ClinicianUIModel = store.PatientProfileCliniciansModels[request.PatientId];

        if (LicencedIntervenor != null) {
            store.PatientProfileCliniciansModels[request.PatientId] = this.MutateIntervenors(
                ClinicianUIModel,
                Id,
                item => {
                    item.updateTitleAndSpecialtyFromLicencedintervenor(LicencedIntervenor);

                    if (item.Model.LicenceOrganisationRule != null)
                        item.IsEditMode = false;
                }
            );
        }
    }

    private Clinicians_SelectSpeciality(request: LocalEvent, store: PatientStore): void {
        const id = request.Get<string>("Id");
        const titleAndSpecialty = request.Get<TitleAndSpecialtyModel>("value");
        const clinicianUIModel = store.PatientProfileCliniciansModels[request.PatientId];

        store.PatientProfileCliniciansModels[request.PatientId] = this.MutateIntervenors(
            clinicianUIModel,
            id,
            item => {
                item.FromSpecialityResult(titleAndSpecialty);

                if (item.Model.LicencedIntervenor != null || item.Employee != null)
                    item.IsEditMode = false;
            }
        );
    }

    private CliniciansEditClick(request: LocalEvent, store: PatientStore): void {
        var OpenAddIntervenorPopup = request.Get<boolean>("OpenAddIntervenorPopup");
        var cliniciansModel = store.PatientProfileCliniciansModels[request.PatientId];

        this.switchToEditMode(store, request.PatientId);

        store.PatientProfileCliniciansModels[request.PatientId] = cliniciansModel;

        if (OpenAddIntervenorPopup == true)
            this.Clinicians_AddIntervenorOpen(request, store);
    }

    private switchToEditMode(store: PatientStore, patientId: string): void {
        const uiModel = store.PatientUIModels[patientId];
        store.PatientUIModels[patientId] = uiModel.set("PatientProfileEditMode", EditMode.Edit);

        let modelToUndo: IPatientCliniciansModel = store.PatientProfileCliniciansModels[patientId];

        const intervenorsOnFile = modelToUndo.get("IntervenorsOnFile").toArray().map(x => x.Clone());
        modelToUndo = modelToUndo.set("IntervenorsOnFile", intervenorsOnFile.toImmutable());

        const allowlist = JSON.parse(JSON.stringify(modelToUndo.get("PatientFileAllowlist")));
        modelToUndo = modelToUndo.set("PatientFileAllowlist", allowlist);

        store.PatientProfileCliniciansUndoModels[patientId] = modelToUndo;
    }

    public CliniciansCancelClick(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientUIModels[request.PatientId];
        const undoModel = store.PatientProfileCliniciansUndoModels[request.PatientId];
        const editedModel = store.PatientProfileCliniciansModels[request.PatientId];

        // Edited health care providers are already saved in the contact directory. Do not undo them.
        undoModel.get("IntervenorsOnFile").forEach(intervenor => {
            const editedHealthCareProviderIntervenor = editedModel.get("IntervenorsOnFile")
                .find(p => p.Model?.HealthCareProvider?.id === intervenor.Model?.HealthCareProvider?.id);

            if (editedHealthCareProviderIntervenor)
                intervenor.Model.HealthCareProvider = editedHealthCareProviderIntervenor.Model.HealthCareProvider;
        });

        undoModel.set("PatientFileAllowlistChanges", { memberIdsToAdd: null, memberIdsToDelete: null });

        uiModel = uiModel.set("PatientProfileEditMode", EditMode.View);

        var patientModel = store.PatientModels[request.PatientId];
        patientModel.IntervenorsOnFile = undoModel.get("IntervenorsOnFile").toArray();
        store.PatientModels[request.PatientId] = patientModel;

        store.PatientUIModels[request.PatientId] = uiModel;
        store.PatientProfileCliniciansModels[request.PatientId] = undoModel;
        delete store.PatientProfileCliniciansUndoModels[request.PatientId];
    }

    private CliniciansSaveClick(request: LocalEvent, store: PatientStore): void {
        const cliniciansModel = store.PatientProfileCliniciansModels[request.PatientId];
        const uiModel = store.PatientUIModels[request.PatientId];
        const intervenors = cliniciansModel.get("IntervenorsOnFile");
        const patientFileAllowlist = cliniciansModel.get("PatientFileAllowlistChanges");

        if (intervenors) {
            const intervenorsDTOs = intervenors.map(x => x.GetNewIntervenorDTO()).toArray();
            store.ActionsProfile.savePatientClinicians(uiModel.get("PatientContactFacetId"), intervenorsDTOs, patientFileAllowlist);
        }
    }

    private updateCliniciansFromFaxDirectory(request: LocalEvent, store: PatientStore): void {
        const healthCareProvider = request.Get<DirectoryHealthCareProvider>("healthCareProvider");
        let patientModel = store.PatientModels[request.PatientId];
        let cliniciansModel = store.PatientProfileCliniciansModels[request.PatientId];
        const licenceOrganisationRule = cacheStore.AllLicenseOrganizationRulesById[healthCareProvider.licenseOrganizationRuleId];
        const licenceOrganisationSpecialisation = cacheStore.TitlesAndSpecialtiesCache
            .toArray()
            .firstOrDefault(p => p.Model.licenceOrganisationSpecialisationModel.Id === healthCareProvider.licenseOrganizationSpecializationId)
            ?.Model
            ?.licenceOrganisationSpecialisationModel;

        patientModel.IntervenorsOnFile = patientModel.IntervenorsOnFile.mutate(
            s => {
                s.Model.HealthCareProvider = healthCareProvider;
                s.Model.LicenceOrganisationRule = licenceOrganisationRule;
                s.Model.LicenceOrganisationSpecialisation = licenceOrganisationSpecialisation;
            },
            s => s.Model.HealthCareProvider && s.Model.HealthCareProvider?.id === healthCareProvider.id
        );

        const clinicianModelIntervenors = cliniciansModel.get("IntervenorsOnFile").mutate(
            s => {
                s.Model.HealthCareProvider = healthCareProvider;
                s.Model.LicenceOrganisationRule = licenceOrganisationRule;
                s.Model.LicenceOrganisationSpecialisation = licenceOrganisationSpecialisation;
            },
            s => s.Model.HealthCareProvider && s.Model.HealthCareProvider?.id === healthCareProvider.id
        );

        store.PatientModels[request.PatientId] = patientModel;
        store.PatientProfileCliniciansModels[request.PatientId] = cliniciansModel.set("IntervenorsOnFile", clinicianModelIntervenors);
    }

    public MutateIntervenors(cliniciansModel: IPatientCliniciansModel, Id: string, Mutation: (item: PatientRefIntervenorWithDetailsModel) => void): IPatientCliniciansModel {
        if (Id == "PendingAddIntervenor") {
            const pendingClinician = cliniciansModel.get("PendingAddIntervenor") ?? PatientRefIntervenorWithDetailsModel.New();
            Mutation(pendingClinician);
            cliniciansModel = cliniciansModel.set("PendingAddIntervenor", pendingClinician);
        }
        else {
            var list = cliniciansModel.get("IntervenorsOnFile").toArray();
            list.forEach(item => {
                if (Id == null || item.Model.Intervenor.Id === Id) {
                    Mutation(item);
                }
            });
            cliniciansModel = cliniciansModel.set("IntervenorsOnFile", list.toImmutable());
        }

        return cliniciansModel;
    }

    private editConsultant(request: LocalEvent, store: PatientStore): void {
        const intervenor = request.Get<PatientRefIntervenorWithDetailsModel>("intervenor");
        const patientId = request.PatientId;
        const clinicianModel = store.PatientProfileCliniciansModels[patientId];

        if (intervenor.Model.LicenceOrganisationRule != null
            && (intervenor.Model.HealthCareProvider
                || intervenor.Model.LicencedIntervenor
                || intervenor.Employee
            )
        ) {
            intervenor.IsEditMode = false;
        }

        const intervenorsOnFile = clinicianModel
            .get("IntervenorsOnFile")
            .toArray()
            .map(p => {
                if (p.Model.Intervenor.Id === intervenor.Model.Intervenor.Id)
                    return intervenor;
                return p;
            });

        store.PatientProfileCliniciansModels[patientId] = clinicianModel.set("IntervenorsOnFile", intervenorsOnFile.toImmutable());
    }

    private cliniciansExternalProviderEdit(request: LocalEvent, store: PatientStore): void {
        const model = request.Get<PatientRefIntervenorWithDetails>("model");
        const id = request.Get<string>("intervenorId");
        let clinicianModel = store.PatientProfileCliniciansModels[request.PatientId];

        if (!model)
            return;

        clinicianModel = clinicianModel.set("PendingAddIntervenor", new PatientRefIntervenorWithDetailsModel(model));
        store.PatientProfileCliniciansModels[request.PatientId] = clinicianModel;

        store.PatientProfileCliniciansModels[request.PatientId] = this.MutateIntervenors(
            store.PatientProfileCliniciansModels[request.PatientId],
            id,
            item => item.Model = model
        );
    }

    private async loadHealthCareProviders(request: LocalEvent, store: PatientStore): Promise<void> {
        if (!cacheStore.ClinicSettings.EnableContactDirectory)
            return;

        const patientContactId = request.Get<number>("patientContactId");
        const patient = store.PatientModels[patientContactId];
        const externalExtervenors = patient?.IntervenorsOnFile.filter(p => !p.IsFromClinic());

        if (!externalExtervenors?.any())
            return;

        const healthCareProviderIds = externalExtervenors
            .map(p => p.Model.Intervenor.HealthCareProviderId)
            .filter(p => !!p);

        // Get legacy id only
        const licencedIntervenorIds = externalExtervenors
            .filter(p => !p.Model.Intervenor.HealthCareProviderId)
            .map(p => p.Model.LicencedIntervenor.Id);

        const healthCareProviders = await this.contactDirectoryClient.getByIds(
            DirectoryEntityType.HealthcareProvider,
            healthCareProviderIds,
            licencedIntervenorIds
        ) as DirectoryHealthCareProviderSearchDto[];

        if (!healthCareProviders?.any())
            return;

        const healthCareProvidersById = healthCareProviders.toDictionary(p => p.id);

        for (const intervenor of externalExtervenors) {
            const healthCareProvider = healthCareProvidersById[intervenor.Model.Intervenor.HealthCareProviderId] as DirectoryHealthCareProviderSearchDto;
            if (healthCareProvider) {
                intervenor.Model.HealthCareProvider = DirectoryHealthCareProvider.fromSearchDto(healthCareProvider);
                intervenor.Model.LicenceOrganisationRule = cacheStore.AllLicenseOrganizationRulesById[healthCareProvider.licenseOrganizationRuleId];
                intervenor.Model.LicenceOrganisationSpecialisation = cacheStore.TitlesAndSpecialtiesCache
                    .toArray()
                    .firstOrDefault(p => p.Model.licenceOrganisationSpecialisationModel.Id === healthCareProvider.licenseOrganizationSpecializationId)
                    ?.Model
                    ?.licenceOrganisationSpecialisationModel;
                continue;
            }

            const legacyHealthCareProvider = healthCareProviders.firstOrDefault(p => !!p.legacyId && p.legacyId.toString() == intervenor.Model.LicencedIntervenor?.Id);
            if (legacyHealthCareProvider) {
                intervenor.Model.HealthCareProvider = legacyHealthCareProvider;
                intervenor.Model.Intervenor.HealthCareProviderId = legacyHealthCareProvider.id;
            }
        }

        let cliniciansModel = store.PatientProfileCliniciansModels[patientContactId];
        cliniciansModel = cliniciansModel.set("IntervenorsOnFile", patient.IntervenorsOnFile.toImmutable());
        store.PatientProfileCliniciansModels[patientContactId] = cliniciansModel;

        store.EmitChangeToListeners();
    }

    private ProtectPatientChartEdit(request: LocalEvent, store: PatientStore): void {
        if (store.PatientUIModels[request.PatientId].get("PatientProfileEditMode") != EditMode.Edit)
            this.switchToEditMode(store, request.PatientId);

        const currentChanges = store.PatientProfileCliniciansModels[request.PatientId].get("PatientFileAllowlistChanges");

        let memberIdsToAdd = request.Get<Guid[] | null>("membersToAdd");
        let memberIdsToDelete = request.Get<Guid[] | null>("membersToDelete");

        const nextAllowlistModel = store.PatientProfileCliniciansModels[request.PatientId].get("PatientFileAllowlist");
        if (memberIdsToAdd != null) {
            nextAllowlistModel.MemberIds = nextAllowlistModel.MemberIds.concat(memberIdsToAdd);
            memberIdsToAdd = memberIdsToAdd.concat(currentChanges?.memberIdsToAdd ?? []);
        }
        if (memberIdsToDelete != null) {
            nextAllowlistModel.MemberIds = nextAllowlistModel.MemberIds.filter(p => !memberIdsToDelete.includes(p));
            memberIdsToDelete = memberIdsToDelete.concat(currentChanges?.memberIdsToDelete ?? []);
        }

        const cliniciansModel = store.PatientProfileCliniciansModels[request.PatientId]
            .set("PatientFileAllowlistChanges", { memberIdsToAdd, memberIdsToDelete })
            .set("PatientFileAllowlist", nextAllowlistModel);
        store.PatientProfileCliniciansModels[request.PatientId] = cliniciansModel;
    }
}

export { PatientProfileCliniciansHandler };
